import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import deviceService, {
  getDeviceAlertRules,
  getDeviceAlertTemplates
} from '../../services/deviceService'
import { Dispatch } from 'redux'
import { createNotification } from './notifications'
import companyService from '../../services/companyService'
import { Device, Sensor } from '../../types'
import { AlertGroup, AlertTemplate } from '../../types/Alerts'
import { AxiosRequestConfig } from 'axios'

interface DeviceState {
  selectedDeviceId: number
  devices: Device[]
  device: Device | undefined
  alertTemplates: AlertTemplate[]
  alertRules: AlertGroup[]
  sensorData: unknown[]
  lastSensorData: Sensor | Record<string, never>
  secondLastSensorData: Sensor | Record<string, never>
  show: boolean
  showDevice: boolean
  loading: boolean
  status: 'idle' | 'loading' | 'failed'
}

export interface SensorData {
  latest: Sensor
  secondLatest: Sensor
  day: Sensor[]
  week: Sensor[]
  allDaily: Sensor[]
  allLatest: Sensor[]
  month: Sensor[]
}

const initialState: DeviceState = {
  selectedDeviceId: 0,
  devices: [],
  device: undefined,
  alertTemplates: [],
  alertRules: [],
  sensorData: [],
  lastSensorData: {},
  secondLastSensorData: {},
  show: false,
  showDevice: false,
  loading: false,
  status: 'idle'
}

export const fetchDevices = createAsyncThunk('devices/fetchDevices', async () => {
  const response = await deviceService.getDevices()
  return response
})

export const fetchDevice = createAsyncThunk('devices/fetchDevice', async (id: number) => {
  const response = await deviceService.getDevice(id)
  return response
})

type SetAlertTemplatesAction = {
  type: string
  payload: AlertTemplate[]
}

type SetAlertRulesAction = {
  type: string
  payload: AlertGroup[]
}

const deviceSlice = createSlice({
  name: 'devices',
  initialState,
  reducers: {
    setSelectedDeviceId(state, action) {
      state.selectedDeviceId = action.payload
    },
    setDevices(state, action) {
      state.devices = action.payload
    },
    setDevice(state, action) {
      state.device = action.payload
    },
    setAlertTemplates(state, action: SetAlertTemplatesAction) {
      state.alertTemplates = action.payload
    },
    setAlertRules(state, action: SetAlertRulesAction) {
      state.alertRules = action.payload
    },
    setDeviceSensorData(state, action) {
      state.sensorData = action.payload
    },
    setLastSensorData(state, action) {
      state.lastSensorData = action.payload
    },
    setSecondLastSensorData(state, action) {
      state.secondLastSensorData = action.payload
    },
    createDevice(state, action) {
      state.devices = [...state.devices, action.payload]
    },
    updateDevice(state, action) {
      state.devices = state.devices.filter((u) => u.id !== action.payload.id).concat(action.payload)
    },
    deleteDevice(state, action) {
      state.devices = state.devices.filter((u) => u.name !== action.payload)
    },
    setShow(state, action) {
      state.show = action.payload
    },
    setShowDevice(state) {
      state.showDevice = !state.showDevice
    },
    setLoading(state, action) {
      state.loading = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDevices.pending, (state, _action) => {
        state.loading = true
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        state.devices = action.payload
        state.loading = false
      })
      .addCase(fetchDevice.pending, (state, _action) => {
        state.loading = true
      })
      .addCase(fetchDevice.fulfilled, (state, action) => {
        state.device = action.payload
        state.loading = false
      })
  }
})

export const {
  setSelectedDeviceId,
  setDevices,
  setDevice,
  setLoading,
  setAlertTemplates,
  setAlertRules
} = deviceSlice.actions

export function getDevices() {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true))
      const response = await deviceService.getDevices()
      dispatch(deviceSlice.actions.setDevices(response))
    } finally {
      dispatch(setLoading(false))
    }
  }
}

export function getDevicesByCompanyId(id: number) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true))
      const response = await companyService.getCompanyDevices(id)
      dispatch(deviceSlice.actions.setDevices(response))
    } catch (_error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(
        createNotification({
          show: true,
          type: 'warning',
          message: 'Something went wrong!',
          timeout: 5000
        })
      )
    } finally {
      dispatch(setLoading(false))
    }
  }
}

export function getProjectDevices(id: number) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true))
      const devices = await deviceService.getProjectDevices(id)

      dispatch(deviceSlice.actions.setDevices(devices))
    } finally {
      dispatch(setLoading(false))
    }
  }
}

export function getDevice(id: string) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true))
      const response = await deviceService.getDevice(id)

      dispatch(deviceSlice.actions.setDevice(response))
    } finally {
      dispatch(setLoading(false))
    }
  }
}

export function setShow(show: boolean) {
  return async (dispatch: Dispatch) => {
    dispatch(deviceSlice.actions.setShow(show))
  }
}

export function setShowDevice() {
  return async (dispatch: Dispatch) => {
    dispatch(deviceSlice.actions.setShowDevice())
  }
}

export function loadDeviceAlertTemplates(config?: AxiosRequestConfig) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true))
      dispatch(setAlertTemplates(await getDeviceAlertTemplates(config)))
    } catch (_error) {
      // Ignore the error for now.
    } finally {
      dispatch(setLoading(false))
    }
  }
}

export function loadDeviceAlertRules(id: string, config?: AxiosRequestConfig) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true))
      dispatch(setAlertRules(await getDeviceAlertRules(id, config)))
    } catch (_error) {
      // Ignore the error for now.
    } finally {
      dispatch(setLoading(false))
    }
  }
}

export default deviceSlice.reducer
